import {
  ArrowRight,
  CalendarCheck,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Mail,
  MapPin,
  PhoneCall,
  Send,
  ShieldCheck,
} from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";
import { scrollToId } from "@/lib/api";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Contact",
    text: "Initial contact and conversation can determine whether we are a good fit with you and your child.",
  },
  {
    icon: MapPin,
    title: "Tour",
    text: "Upon request we can set up a time to meet you and your child where you can see one of our centers.",
  },
  {
    icon: FileText,
    title: "Intake Paperwork",
    text: "Once we determine that we are a good fit there is a set of initial paperwork — when you are pulled from the waitlist additional paperwork may be required.",
  },
  {
    icon: ShieldCheck,
    title: "Authorization",
    text: "Our team will work with you and your physicians to get authorization from your insurance for an initial assessment.",
  },
  {
    icon: ClipboardList,
    title: "Assessment",
    text: "A BCBA spends time with your child to observe behaviors and skills to write an individualized treatment plan.",
  },
  {
    icon: ClipboardCheck,
    title: "Treatment Plan Clinical Review",
    text: "Your assigned BCBA writes an individualized treatment plan that includes skill acquisition and behavioral interventions. These are reviewed by our clinical director and your family before submitting to insurance.",
  },
  {
    icon: Send,
    title: "Submit Final Plan for Insurance Approval",
    text: "This process takes approximately two weeks. During this time, a tentative start date will be discussed.",
  },
  {
    icon: CalendarCheck,
    title: "Agree On Start Date",
    text: "We look forward to working with your family!",
  },
];

const INSURERS = [
  "TX Medicaid Plans",
  "Cigna",
  "UHC/Optum",
  "BCBS",
  "Humana",
  "Magellan",
  "Compsych",
  "Aetna",
  "Multiplan",
];

export const Insurance = () => (
  <section id="insurance" data-testid="insurance-section" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <ChapterHeading
        number="03"
        eyebrow="Onboarding & Coverage"
        title="A smooth start,"
        accent="step by step."
        testid="insurance-heading"
      />

      <Reveal>
        <p className="text-base md:text-lg leading-relaxed text-brand-muted max-w-3xl mb-16" data-testid="onboarding-welcome">
          Welcome to our onboarding journey! This structured process ensures a smooth start to
          supporting your child's behavioral health needs through assessment, personalized planning,
          and collaboration with your family, physicians, and insurance providers.
        </p>
      </Reveal>

      <Reveal>
        <div className="rounded-[2.5rem] bg-brand-blue text-white shadow-lift p-10 md:p-14 mb-20" data-testid="special-offer-banner">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full bg-brand-yellow text-brand-ink text-xs font-bold uppercase tracking-[0.18em] px-5 py-2 mb-6">
                October & November Special
              </span>
              <h3 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
                Short-Term Self-Pay Program
              </h3>
              <p className="text-white/85 text-base md:text-lg leading-relaxed">
                16 hours a week — 4 hours, 4 days a week —{" "}
                <span className="font-semibold text-brand-yellow">$600 weekly</span>. We want to make
                therapy and support accessible to parents waiting for coverage. Contact us via email
                or the intake form — we would love to support your family.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`mailto:${EMAIL}?subject=Short-Term%20Self-Pay%20Program`}
                data-testid="special-offer-email"
                className="inline-flex items-center gap-2 rounded-full bg-white text-brand-blue font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <Mail className="h-4 w-4" /> Email Us
              </a>
              <button
                data-testid="special-offer-intake"
                onClick={() => scrollToId("intake")}
                className="group inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold px-8 py-4 transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-white/10"
              >
                Start Intake Form
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <Reveal>
            <h3 className="font-serif text-3xl md:text-4xl font-semibold text-brand-blue mb-10" data-testid="onboarding-process-heading">
              The Onboarding Process
            </h3>
          </Reveal>
          <div className="relative" data-testid="onboarding-steps">
            <div className="absolute left-6 top-2 bottom-2 w-px bg-brand-lightblue/60" aria-hidden="true" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={0.05 * i}>
                <div className="relative flex gap-6 pb-9 last:pb-0" data-testid={`onboarding-step-${i + 1}`}>
                  <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white border border-brand-lightblue/50 shadow-soft text-brand-blue">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <div className="pt-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-coral">
                      Step {String(i + 1).padStart(2, "0")}
                    </span>
                    <h4 className="font-serif text-xl md:text-2xl font-semibold text-brand-blue mt-1 mb-1.5">
                      {s.title}
                    </h4>
                    <p className="text-sm md:text-base leading-relaxed text-brand-muted">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-brand-muted max-w-md" data-testid="onboarding-footnote">
              *Actual timing may vary based on insurance requirements and possible staffing
              arrangements for in-home treatments.
            </p>
          </Reveal>
        </div>

        <div className="space-y-8">
          <Reveal>
            <div className="rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-lift p-8 md:p-10" data-testid="coverage-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-pale text-brand-blue">
                  <ShieldCheck className="h-6 w-6" />
                </span>
                <h3 className="font-serif text-2xl md:text-3xl font-semibold text-brand-blue">
                  Insurance Coverage
                </h3>
              </div>
              <p className="text-sm md:text-base text-brand-muted leading-relaxed mb-6">
                We accept most major insurance providers:
              </p>
              <div className="flex flex-wrap gap-3 mb-8" data-testid="coverage-insurers">
                {INSURERS.map((ins) =>
                  ins === "TX Medicaid Plans" ? (
                    <span
                      key={ins}
                      data-testid="insurer-tx-medicaid-pending"
                      className="rounded-full border border-brand-yellow bg-brand-yellow/40 px-5 py-2 text-sm font-semibold text-brand-ink transition-[transform] duration-300 hover:-translate-y-0.5"
                    >
                      TX Medicaid Plans <span className="font-normal opacity-75">— Pending</span>
                    </span>
                  ) : (
                    <span
                      key={ins}
                      className="rounded-full border border-brand-lightblue/60 bg-brand-pale/50 px-5 py-2 text-sm font-medium text-brand-blue transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-pale"
                    >
                      {ins}
                    </span>
                  )
                )}
              </div>
              <div className="rounded-2xl bg-brand-pale/60 border border-brand-lightblue/40 px-6 py-5 mb-5">
                <p className="text-sm md:text-base leading-relaxed text-brand-ink" data-testid="coverage-verify-note">
                  <span className="font-semibold">Have questions about coverage?</span> We will call
                  your insurance to verify benefits and confirm expected out-of-pocket expenses for
                  you.
                </p>
              </div>
              <p className="text-sm leading-relaxed text-brand-muted" data-testid="coverage-private-note">
                We also work with families who choose to pay privately for therapy. Contact us today
                to find out more information about your specific insurance questions. Insurance
                credentialing is in progress with additional plans — reach out for the latest
                participating plan list.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft overflow-hidden" data-testid="payment-options-card">
              <div className="px-8 py-6 border-b border-brand-lightblue/30 bg-brand-pale/40">
                <h3 className="font-serif text-2xl font-semibold text-brand-blue" data-testid="payment-options-title">
                  Self-Pay Options
                </h3>
              </div>
              <ul data-testid="payment-options-list">
                <li className="flex flex-wrap items-center justify-between gap-3 px-8 py-5 border-b border-brand-lightblue/20" data-testid="payment-option-self-pay-6">
                  <span className="font-medium text-brand-ink">Self-Pay ABA — 6 Hours/Week</span>
                  <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-4 py-1.5">Available</span>
                </li>
                <li className="flex flex-wrap items-center justify-between gap-3 px-8 py-5 border-b border-brand-lightblue/20" data-testid="payment-option-self-pay-8">
                  <span className="font-medium text-brand-ink">Self-Pay ABA — 8 Hours/Week</span>
                  <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-4 py-1.5">Available</span>
                </li>
                <li className="flex flex-wrap items-center justify-between gap-3 px-8 py-5 border-b border-brand-lightblue/20" data-testid="payment-option-tx-medicaid">
                  <span className="font-medium text-brand-ink">Texas Medicaid Plans</span>
                  <span className="rounded-full bg-brand-yellow/50 border border-brand-yellow text-brand-ink text-xs font-semibold px-4 py-1.5">Pending</span>
                </li>
                <li className="flex flex-wrap items-center justify-between gap-3 px-8 py-5" data-testid="payment-option-self-pay-16">
                  <span className="font-medium text-brand-ink">Short-Term Program — 16 Hours/Week</span>
                  <span className="rounded-full bg-brand-yellow/50 border border-brand-yellow text-brand-ink text-xs font-semibold px-4 py-1.5">$600/Week — Oct & Nov Special</span>
                </li>
              </ul>
              <div className="px-8 py-5 bg-brand-coralpale/60 border-t border-brand-coral/20">
                <p className="text-sm leading-relaxed text-brand-muted" data-testid="payment-options-note">
                  ABA therapy recommendations and hours are individualized based on each child's
                  needs and BCBA clinical assessment.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <button
              data-testid="insurance-cta-contact"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-brand-deep"
            >
              Ask Us About Coverage
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

